import type { Priority, SlaView } from './types';
import { formatClock } from './types';

/**
 * HOW LONG SOMETHING HAS LEFT — one answer, from whichever deadline comes first.
 *
 * A piece of work can carry two: a due date somebody set, and an SLA clock running against
 * the agreement. Lists used to show them in two columns and sort by one, so a ticket two
 * hours from breaching sat below a task due next month because its due date was empty.
 * "What do I do first" is about the nearer of the two, so that is the one this reads.
 *
 * A due date is counted to the END of that working day — 16:30, or 16:00 on a Friday — in
 * South African time, because "due Tuesday" means by close of business Tuesday, not by the
 * midnight that starts it.
 */
export interface TimeLeft {
  /** The deadline as a timestamp, for sorting. Null when nothing is due. */
  at: number | null;
  /** "2h 10m left", "due today", "in 3 days", "4 days late" — or "" when nothing is due. */
  label: string;
  late: boolean;
  /** Inside a working day of its deadline, and not already late. */
  soon: boolean;
}

const DAY = 86_400_000;

/** End of the working day on a date, in South African time. */
export function closeOfBusiness(date: string): number {
  const friday = new Date(`${date}T12:00:00+02:00`).getUTCDay() === 5;
  return Date.parse(`${date}T${friday ? '16:00' : '16:30'}:00+02:00`);
}

/** Whole days from one `YYYY-MM-DD` to another. */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / DAY);
}

export function timeLeft(
  item: { dueDate: string | null; sla: SlaView | null; completedAt?: string | null; closedAt?: string | null },
  today: string,
): TimeLeft {
  const none: TimeLeft = { at: null, label: '', late: false, soon: false };
  // Finished work has nothing left to run out of.
  if (item.completedAt || item.closedAt) return none;

  const clock = item.sla?.resolution && item.sla.resolution.remainingMinutes !== null ? item.sla.resolution : null;
  const fromClock: TimeLeft | null = clock
    ? {
        at: Date.parse(clock.due),
        label: slaLabel(clock.remainingMinutes!),
        late: clock.remainingMinutes! < 0,
        soon: clock.remainingMinutes! >= 0 && clock.remainingMinutes! <= 8 * 60,
      }
    : null;

  const fromDate: TimeLeft | null = item.dueDate
    ? (() => {
        const days = daysBetween(today, item.dueDate);
        const label =
          days < 0
            ? `${-days} day${days === -1 ? '' : 's'} late`
            : days === 0
              ? 'due today'
              : days === 1
                ? 'due tomorrow'
                : `in ${days} days`;
        return { at: closeOfBusiness(item.dueDate), label, late: days < 0, soon: days >= 0 && days <= 1 };
      })()
    : null;

  if (fromClock && fromDate) return fromClock.at! <= fromDate.at! ? fromClock : fromDate;
  return fromClock ?? fromDate ?? none;
}

/** A working day, in minutes — Monday to Thursday's 08:00–16:30. */
const WORKING_DAY = 510;

/**
 * "SLA 2h 10m left", "SLA 3 days over". Hours and minutes inside a working day, whole working
 * days past one — "242h 34m over" is a number nobody can picture. Said as the SLA so it is
 * never mistaken for the due date beside it.
 */
function slaLabel(minutes: number): string {
  const abs = Math.abs(minutes);
  const amount = abs < WORKING_DAY ? formatClock(abs) : `${Math.round(abs / WORKING_DAY)} working day${Math.round(abs / WORKING_DAY) === 1 ? '' : 's'}`;
  return minutes < 0 ? `SLA ${amount} over` : `SLA ${amount} left`;
}

const PRIORITY_RANK: Record<Priority, number> = { critical: 0, high: 1, medium: 2, low: 3, none: 4 };

/**
 * WHAT TO DO FIRST: priority, then whichever has least time left, then nothing due last.
 *
 * One number, so a table that sorts by a single column can sort by both — a rank in the high
 * digits and the deadline under it. Two thousand years of milliseconds fit under 10^14.
 */
export function urgencyKey(priority: Priority, left: TimeLeft): number {
  const deadline = left.at === null ? 9e13 : Math.min(Math.max(left.at, 0), 9e13);
  return PRIORITY_RANK[priority] * 1e14 + deadline;
}

/** The deadline alone, nothing due last — for sorting by time left regardless of priority. */
export function deadlineKey(left: TimeLeft): number {
  return left.at === null ? Number.MAX_SAFE_INTEGER : left.at;
}
